import { NovenaType, ChapletType } from '../types'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { NOVENA_INFO } from './novenas';
import { CHAPLET_INFO } from './chaplets';

export interface SearchResult {
  id: string;
  type: 'novena' | 'chaplet';
  name: string;
  description: string;
  patron?: string;
  feastDay?: string;
  color: string;
  icon: string;
  searchableText: string; // Combined text for searching (lowercase)
}

/**
 * Build searchable index from all novenas and chaplets
 * This creates a flat, searchable array that can be filtered efficiently
 */
export const buildSearchIndex = (): SearchResult[] => {
  const results: SearchResult[] = [];

  // Index all novenas
  Object.entries(NOVENA_INFO).forEach(([key, info]) => {
    const searchableText = `${info.name} ${info.description} ${info.patron || ''} ${info.feastDay || ''}`.toLowerCase();
    
    results.push({
      id: key,
      type: 'novena',
      name: info.name,
      description: info.description,
      patron: info.patron,
      feastDay: info.feastDay,
      color: info.color,
      icon: info.icon,
      searchableText
    });
  });

  // Index all chaplets
  Object.entries(CHAPLET_INFO).forEach(([key, info]) => {
    const searchableText = `${info.name} ${info.description}`.toLowerCase();
    
    results.push({
      id: key,
      type: 'chaplet',
      name: info.name,
      description: info.description,
      color: info.color,
      icon: info.icon,
      searchableText
    });
  });

  return results;
};

// Build the index once at module load time
export const SEARCH_INDEX = buildSearchIndex();

/**
 * Normalize text for searching - remove punctuation and extra spaces
 */
const normalizeSearchText = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[.,\-''""]/g, '') // Remove common punctuation
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
};

/**
 * Search through the index
 * Smart substring matching with case insensitivity and punctuation tolerance
 * Results are ranked: exact name matches first, then description matches
 */
export const searchPrayers = (query: string): SearchResult[] => {
  if (!query.trim()) return [];

  const searchTerm = normalizeSearchText(query);
  
  // Score results for better relevance
  const scored = SEARCH_INDEX.map(item => {
    let score = 0;
    
    const normalizedName = normalizeSearchText(item.name);
    const normalizedPatron = item.patron ? normalizeSearchText(item.patron) : '';
    const normalizedFeast = item.feastDay ? normalizeSearchText(item.feastDay) : '';

    // Exact name match gets highest score
    if (normalizedName === searchTerm) {
      score = 1000;
    }
    // Name starts with search term
    else if (normalizedName.startsWith(searchTerm)) {
      score = 500;
    }
    // Name contains search term
    else if (normalizedName.includes(searchTerm)) {
      score = 300;
    }
    // Found in patron/feast day
    else if (normalizedPatron.includes(searchTerm) || normalizedFeast.includes(searchTerm)) {
      score = 200;
    }
    // Found in description
    else if (normalizeSearchText(item.searchableText).includes(searchTerm)) {
      score = 100;
    }
    
    return { item, score };
  });

  // Filter out non-matches and sort by score
  return scored
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
};
